"use client"

import { useState } from "react"
import { useSession } from "next-auth/react"
import { MessageCircle, X, Send, CheckCircle } from "lucide-react"
import Link from "next/link"

type UIState = "closed" | "open" | "sending" | "sent"

export default function SupportChatBubble() {
  const { data: session, status } = useSession()
  const [uiState, setUiState] = useState<UIState>("closed")
  const [message, setMessage] = useState("")
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function send() {
    if (!message.trim()) return
    setError(null)
    setUiState("sending")
    try {
      const res = await fetch("/api/support/message", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: message.trim() }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || "Failed to send")
      setConversationId(data.conversationId)
      setUiState("sent")
      setMessage("")
    } catch (err: any) {
      setError(err.message || "Something went wrong. Please try again.")
      setUiState("open")
    }
  }

  function toggle() {
    if (uiState === "closed") {
      setUiState("open")
    } else {
      setUiState("closed")
    }
  }

  // Only show for logged-in users
  if (status !== "loading" && !session) return null

  return (
    <div className="fixed right-4 bottom-[calc(56px+env(safe-area-inset-bottom)+12px)] md:bottom-6 z-40 flex flex-col items-end gap-2">
      {/* Popover card */}
      {uiState !== "closed" && (
        <div className="w-72 bg-[#1e293b] border border-[#334155] rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-sky-500">
            <div>
              <p className="text-white font-semibold text-sm">Contact Support</p>
              <p className="text-sky-100 text-xs">We typically reply within a day</p>
            </div>
            <button
              onClick={() => setUiState("closed")}
              className="text-white/80 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="p-4">
            {status === "loading" ? (
              <p className="text-slate-400 text-sm text-center py-2">Loading…</p>
            ) : !session ? (
              <div className="text-center space-y-3">
                <p className="text-slate-300 text-sm leading-relaxed">
                  Have a question, feedback, or need help? Log in to send us a message.
                </p>
                <Link
                  href="/login"
                  className="block w-full text-center bg-sky-500 hover:bg-sky-400 text-white text-sm font-medium py-2.5 rounded-lg transition-colors"
                >
                  Log in to message us
                </Link>
              </div>
            ) : uiState === "sent" ? (
              <div className="text-center space-y-3 py-2">
                <CheckCircle className="mx-auto text-green-400" size={32} />
                <p className="text-white font-medium text-sm">Message sent!</p>
                <p className="text-slate-400 text-xs">We'll get back to you soon.</p>
                {conversationId && (
                  <Link
                    href={`/messages/conversation/${conversationId}`}
                    onClick={() => setUiState("closed")}
                    className="block text-sky-400 text-xs hover:text-sky-300 transition-colors"
                  >
                    View conversation →
                  </Link>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-slate-300 text-sm leading-relaxed">
                  Questions, feedback, or need help? Send us a message.
                </p>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message…"
                  rows={4}
                  className="w-full bg-[#0f172a] border border-[#334155] text-white placeholder-slate-500 text-sm rounded-lg px-3 py-2 resize-none focus:outline-none focus:border-sky-500 transition-colors"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send()
                  }}
                />
                {error && <p className="text-red-400 text-xs">{error}</p>}
                <button
                  onClick={send}
                  disabled={!message.trim() || uiState === "sending"}
                  className="w-full flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-2.5 rounded-lg transition-colors"
                >
                  <Send size={14} />
                  {uiState === "sending" ? "Sending…" : "Send message"}
                </button>
                <p className="text-slate-500 text-xs text-center">
                  ⌘ + Enter to send
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Floating bubble button */}
      <button
        onClick={toggle}
        className="w-13 h-13 w-[52px] h-[52px] rounded-full bg-sky-500 hover:bg-sky-400 shadow-lg shadow-sky-500/30 flex items-center justify-center transition-all active:scale-95"
        aria-label={uiState === "closed" ? "Open support chat" : "Close support chat"}
      >
        {uiState === "closed" ? (
          <MessageCircle size={22} className="text-white" />
        ) : (
          <X size={22} className="text-white" />
        )}
      </button>
    </div>
  )
}
